import { useEffect, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
import {
  Heading,
  Text,
  Link,
  Strong,
  Em,
  Code,
  Blockquote,
  Separator,
} from "@radix-ui/themes";

interface MarkdownViewerProps {
  contentKey: string;
}

// Content imports - add new content files here
const contentModules: Record<
  string,
  Record<string, () => Promise<{ default: string }>>
> = {
  "privacy-policy": {
    en: () => import("../../content/privacy-policy_en.md?raw"),
    fr: () => import("../../content/privacy-policy_fr.md?raw"),
  },
  "terms-of-use": {
    en: () => import("../../content/terms-of-use_en.md?raw"),
    fr: () => import("../../content/terms-of-use_fr.md?raw"),
  },
};

// Custom components for react-markdown using Radix UI
const markdownComponents: Components = {
  h1: ({ children }) => (
    <Heading as="h1" size="8" mb="4" mt="6">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as="h2" size="6" mb="3" mt="5">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading as="h3" size="5" mb="2" mt="4">
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading as="h4" size="4" mb="2" mt="3">
      {children}
    </Heading>
  ),
  p: ({ children }) => (
    <Text as="p" size="3" mb="3" style={{ lineHeight: 1.7 }}>
      {children}
    </Text>
  ),
  a: ({ href, children }) => (
    <Link href={href} target={href?.startsWith("http") ? "_blank" : undefined}>
      {children}
    </Link>
  ),
  strong: ({ children }) => <Strong>{children}</Strong>,
  em: ({ children }) => <Em>{children}</Em>,
  code: ({ children }) => (
    <Code variant="soft" size="2">
      {children}
    </Code>
  ),
  blockquote: ({ children }) => (
    <Blockquote size="3" mb="3">
      {children}
    </Blockquote>
  ),
  hr: () => <Separator size="4" my="5" />,
  ul: ({ children }) => (
    <ul
      style={{
        paddingLeft: "1.5rem",
        marginBottom: "1rem",
        listStyleType: "disc",
      }}
    >
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol
      style={{
        paddingLeft: "1.5rem",
        marginBottom: "1rem",
        listStyleType: "decimal",
      }}
    >
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: "0.5rem", lineHeight: 1.6 }}>
      <Text size="3">{children}</Text>
    </li>
  ),
};

export function MarkdownViewer({ contentKey }: MarkdownViewerProps) {
  const { i18n } = useTranslation();
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadContent() {
      setLoading(true);
      setError(null);

      const locale = i18n.language.startsWith("fr") ? "fr" : "en";
      const contentLoader = contentModules[contentKey]?.[locale];

      if (!contentLoader) {
        setError(`Content not found: ${contentKey}`);
        setLoading(false);
        return;
      }

      try {
        const module = await contentLoader();
        setContent(module.default);
      } catch (err) {
        console.error("Failed to load markdown content:", err);
        setError("Failed to load content");
      } finally {
        setLoading(false);
      }
    }

    loadContent();
  }, [contentKey, i18n.language]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>{error}</div>;
  }

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {content}
    </ReactMarkdown>
  );
}
